import { useEffect } from "react";
import { X } from "lucide-react";

const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    intro:
      'ARQITEK ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.',
    sections: [
      {
        heading: "Information We Collect",
        body:
          "We collect information you provide directly: name, email address, company details, phone number, and project data you enter into the platform. We also collect usage data automatically, including IP address, browser type, device information, and interaction patterns through cookies and analytics.",
      },
      {
        heading: "How We Use Your Information",
        body:
          "We use your data to operate and improve the ARQITEK platform, process transactions, send service communications, provide AI-powered features (design concepts, specifications, pricing estimates), and deliver customer support. We may use aggregated, anonymized data for analytics and product improvement.",
      },
      {
        heading: "AI Data Processing",
        body:
          "When you use Angela AI features, your project descriptions, reference images, and conversation data are processed to generate insights and recommendations. This data is not used to train third-party models without your consent.",
      },
      {
        heading: "Data Sharing",
        body:
          "We do not sell your personal information. We share data only with trusted service providers who help us operate the platform, and when required by law or to protect our rights.",
      },
      {
        heading: "Data Security",
        body:
          "We implement industry-standard safeguards to protect your information. However, no method of transmission over the internet or electronic storage is fully secure, and we cannot guarantee absolute security.",
      },
      {
        heading: "Your Rights",
        body:
          "You may access, correct, or delete your personal data at any time. To exercise these rights, contact us at privacy@arqitek.co.",
      },
      {
        heading: "Contact Us",
        body:
          "If you have questions about this Privacy Policy, please reach out to us at privacy@arqitek.co.",
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "Last updated: June 2026",
    intro:
      "These Terms of Service govern your access to and use of the ARQITEK platform. By using our services, you agree to these terms in full.",
    sections: [
      {
        heading: "Acceptance of Terms",
        body:
          "By accessing or using the ARQITEK platform, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree, you may not use the platform.",
      },
      {
        heading: "Use of the Platform",
        body:
          "You agree to use the platform only for lawful purposes and in accordance with these terms. You are responsible for all activity that occurs under your account and for maintaining the confidentiality of your credentials.",
      },
      {
        heading: "Accounts",
        body:
          "You must provide accurate and complete information when creating an account. We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent or abusive activity.",
      },
      {
        heading: "Intellectual Property",
        body:
          "All content, software, and materials on the platform, including the ARQITEK name and logo, are owned by or licensed to us and protected by intellectual property laws. You retain ownership of the project data you provide.",
      },
      {
        heading: "AI-Generated Content",
        body:
          "Design concepts, specifications, pricing estimates, and other outputs generated by Angela AI are provided for informational purposes and should be independently verified by qualified professionals before relying on them.",
      },
      {
        heading: "Limitation of Liability",
        body:
          "The platform is provided \u201cas is\u201d without warranties of any kind. To the fullest extent permitted by law, ARQITEK is not liable for any indirect, incidental, or consequential damages arising from your use of the platform.",
      },
      {
        heading: "Termination",
        body:
          "We may suspend or terminate your access to the platform at any time, with or without notice, for conduct that we believe violates these terms or is harmful to other users or to us.",
      },
      {
        heading: "Changes to These Terms",
        body:
          "We may update these Terms of Service from time to time. Continued use of the platform after changes take effect constitutes acceptance of the revised terms.",
      },
      {
        heading: "Contact",
        body:
          "For questions about these Terms of Service, contact us at legal@arqitek.co.",
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Last updated: June 2026",
    intro:
      "This Cookie Policy explains how ARQITEK uses cookies and similar technologies to recognize you when you visit our platform.",
    sections: [
      {
        heading: "What Are Cookies",
        body:
          "Cookies are small data files placed on your device when you visit a website. They are widely used to make websites work, or work more efficiently, and to provide reporting information.",
      },
      {
        heading: "How We Use Cookies",
        body:
          "We use essential cookies to keep you signed in and secure the platform, analytics cookies to understand how the platform is used, and preference cookies to remember your settings and choices.",
      },
      {
        heading: "Managing Cookies",
        body:
          "You can control and delete cookies through your browser settings. Note that disabling certain cookies may affect the functionality of the platform. Our cookie consent banner also lets you accept or decline non-essential cookies.",
      },
      {
        heading: "Contact",
        body:
          "If you have questions about our use of cookies, contact us at privacy@arqitek.co.",
      },
    ],
  },
};

export function LegalModal({ doc, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!doc) return null;
  const content = LEGAL_DOCS[doc];
  if (!content) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      data-testid="legal-modal"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="legal-modal-overlay" />
      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#0B1211] border border-white/10 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-7 md:px-9 py-6 border-b border-white/10">
          <h2 className="font-clash text-2xl font-semibold text-white" data-testid="legal-modal-title">{content.title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="legal-modal-close"
            className="text-[#8AA09A] hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-7 md:px-9 py-7 space-y-6" data-testid="legal-modal-body">
          <p className="text-sm text-[#8AA09A]">{content.updated}</p>
          <p className="text-sm leading-relaxed text-[#C4D0CC]">{content.intro}</p>
          {content.sections.map((s) => (
            <div key={s.heading}>
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-white mb-2">{s.heading}</h3>
              <p className="text-sm leading-relaxed text-[#C4D0CC]">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
